import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { StyleObject, useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.activation.summary_cards')

// AI-890: the Figma "Change" control is a near-black OUTLINED box, not the blue
// uikit `secondary` kind (whose border + label are primary400 blue). uikit's
// Button spreads a consumer `overrides.BaseButton` LAST, REPLACING its own
// kind/size preset — a partial override would strip the box geometry — so we
// restate the COMPLETE small-secondary box here, recolored onto grey90 (the
// design system's black text/border token). Same idiom + rationale as
// WizardNavButtons' CONTINUE_BUTTON_OVERRIDES. This button is never disabled
// (the page hides it entirely for non-editors via showChange), so no disabled
// styling is restated. Typed as StyleObject so the CSS-keyword literals (e.g.
// borderStyle 'solid') keep their narrow types and satisfy ButtonOverrides.
const changeButtonStyle: StyleObject = {
  backgroundColor: 'transparent',
  color: palette.grey90,
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopWidth: '2px',
  borderRightWidth: '2px',
  borderBottomWidth: '2px',
  borderLeftWidth: '2px',
  borderTopColor: palette.grey90,
  borderRightColor: palette.grey90,
  borderBottomColor: palette.grey90,
  borderLeftColor: palette.grey90,
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  borderBottomLeftRadius: '4px',
  borderBottomRightRadius: '4px',
  paddingTop: '0.125rem',
  paddingBottom: '0.125rem',
  paddingLeft: '1rem',
  paddingRight: '1rem',
  fontSize: '0.875rem',
  fontWeight: 700,
  lineHeight: '1.5rem',
  ':hover:enabled': { backgroundColor: palette.grey10 },
  ':active:enabled': { backgroundColor: palette.grey20 },
}

const CHANGE_BUTTON_OVERRIDES = {
  BaseButton: { style: changeButtonStyle },
}

// The configured-summary card keys (AI-22). Each key drives its localized CATEGORY
// label (the small uppercase text) and its "Change" aria-label; the page
// (ConfiguredSummary) owns each card's bold setting, long description, and which
// config-modal row its "Change" opens.
export type ConfigCardKey =
  | 'philosophy'
  | 'brand_voice'
  | 'active_locations'
  | 'approved_rewards'
  | 'escalation'

export interface ConfigSummaryCardItem {
  key: ConfigCardKey
  // The bold setting value (e.g. "Balanced", "Piloting at 6 locations"). The page
  // derives this from the live config so these cards stay purely presentational.
  title: React.ReactNode
  // The long descriptive paragraph beneath the setting.
  description: React.ReactNode
  // Open this card's editor (the page owns the card → config-modal row mapping).
  onChange: () => void
}

interface Props {
  cards: ConfigSummaryCardItem[]
  // Render the per-card "Change" control. Defaults to true. The page hides these
  // for non-admin preparers: three of the five editors carry no CASL check of
  // their own, so a modal whose inputs all look live is worse than no button.
  // The shared shell also gates Save on canManage, so this is one of two gates
  // rather than the only one (see ConfiguredSummary).
  showChange?: boolean
}

// Presentational only — no data fetching / no useDispatch. Renders each configured
// area as a card with a green CIRCULAR check, a small uppercase category label, the
// bold current setting, and a descriptive paragraph. The "Change" control is a
// near-black OUTLINED box (grey90 border + label per Figma, AI-890 — see
// CHANGE_BUTTON_OVERRIDES), not a text link, carrying a contextual per-card
// aria-label.
const ConfigSummaryCards = ({ cards, showChange = true }: Props) => {
  const [css] = useStyletron()

  const categoryClass = css({
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  })

  return (
    <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
      {cards.map(card => {
        const category = t(`${card.key}.title`)
        return (
          <div
            key={card.key}
            className={`d-flex justify-content-between align-items-start ${css({
              backgroundColor: 'white',
              border: `1px solid ${palette.grey20}`,
              borderRadius: '4px',
              padding: '24px',
              gap: '16px',
            })}`}
          >
            <div className="d-flex align-items-start">
              <i
                className={`fa fa-check-circle ${css({
                  color: palette.nephritis,
                  marginRight: '12px',
                  fontSize: '20px',
                  marginTop: '2px',
                })}`}
                aria-hidden="true"
              />
              <div className="d-flex flex-column">
                <Text.BodyText5 bold color="grey50" className={categoryClass}>
                  {category}
                </Text.BodyText5>
                <Text.Header4 bold>{card.title}</Text.Header4>
                <div className={css({ marginTop: '4px' })}>
                  {card.description}
                </div>
              </div>
            </div>
            {showChange && (
              <Button
                // AI-890: near-black outlined box (not the blue `secondary`
                // kind). The override REPLACES uikit's kind/size preset, so no
                // `kind`/`size` prop is passed — the box is fully restated in
                // CHANGE_BUTTON_OVERRIDES.
                overrides={CHANGE_BUTTON_OVERRIDES}
                // Signals to AT that activating this opens a modal dialog (a
                // focus context shift), matching the Agent-Tuning rows. No
                // aria-expanded: the dialog is not disclosed content, and it is
                // not in the DOM until opened, so aria-controls would dangle.
                aria-haspopup="dialog"
                onClick={card.onChange}
                aria-label={t('change_aria', { item: category })}
              >
                {t('change')}
              </Button>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ConfigSummaryCards
